import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { NamedLink } from '../../../components';

import css from './HaulerEquipmentBanner.module.css';

/**
 * Hauler-only control above the job board.
 *
 * Two states, both about the same thing — getting this hauler in front of work they can do:
 *
 *  1. They have declared their equipment → an OPT-IN "only jobs I'm equipped for" toggle.
 *  2. They have not (they signed up before the field existed) → a non-blocking prompt to add it.
 *     They are never locked out; until then they match on their vehicleType alone.
 *
 * ⚠️ The toggle is opt-in, and says plainly what it hides, because of how Sharetribe value filters
 * work: a filter matches ONLY listings that SET the field. Every job posted before job scoping
 * shipped — and any job where the AI did not run — is ABSENT from the filtered view rather than
 * ranked lower. Defaulting this on would silently empty the board.
 *
 * @component
 * @param {Object} props
 * @param {string[]} props.haulerEquipment - the hauler's effective kit
 * @param {boolean} props.hasDeclaredEquipment - whether they actually answered the equipment question
 * @param {boolean} props.isActive - whether the filter is currently applied
 * @param {Function} props.onToggle - (nextActive: boolean) => void
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
const HaulerEquipmentBanner = props => {
  const { haulerEquipment, hasDeclaredEquipment, isActive, onToggle, className } = props;

  if (!hasDeclaredEquipment) {
    return (
      <div className={classNames(css.root, css.prompt, className)}>
        <span className={css.text}>
          <FormattedMessage id="HaulerEquipmentBanner.addEquipmentPrompt" />
        </span>
        <NamedLink name="ProfileSettingsPage" className={css.action}>
          <FormattedMessage id="HaulerEquipmentBanner.addEquipmentLink" />
        </NamedLink>
      </div>
    );
  }

  // Nothing to filter by. Can happen if a hauler's declared kit is somehow empty.
  if (!haulerEquipment || haulerEquipment.length === 0) {
    return null;
  }

  return (
    <div className={classNames(css.root, className)}>
      <label className={css.toggleLabel} htmlFor="haulerEquipmentToggle">
        <input
          id="haulerEquipmentToggle"
          type="checkbox"
          className={css.checkbox}
          checked={isActive}
          onChange={e => onToggle(e.target.checked)}
        />
        <span className={css.text}>
          <FormattedMessage id="HaulerEquipmentBanner.toggleLabel" />
        </span>
      </label>
      {isActive ? (
        <span className={css.caveat}>
          <FormattedMessage id="HaulerEquipmentBanner.toggleCaveat" />
        </span>
      ) : null}
    </div>
  );
};

export default HaulerEquipmentBanner;
